'use client';

import type { ReactNode } from 'react';

import classnames from 'classnames';

import type { BaseComponentProps } from '../base-component.types';

import styles from './ResourceBar.module.scss';

export type ResourceBarProps = BaseComponentProps & {
  /** Which edge of the resource the bar is pinned over. */
  edge: 'top' | 'bottom';
  /**
   * `thirds` gives the middle slot the centre of the bar itself: the columns either
   * side are equal, so however many icons they hold, what is centred stays centred.
   * `split` drops the middle and lets the leading slot take the whole run up to the
   * trailing one, for a bar whose content stretches — a progress bar.
   */
  layout?: 'thirds' | 'split';
  start?: ReactNode;
  center?: ReactNode;
  end?: ReactNode;
};

/**
 * The strip a resource is topped and tailed with. It floats over the body rather than
 * taking room from it, so content dissolves underneath on its way out, and it carries
 * nothing of its own — every bar in the kit is this shell with different things in its
 * slots.
 */
function ResourceBar({
  style,
  className,
  edge,
  layout = 'thirds',
  start,
  center,
  end,
  testId,
}: ResourceBarProps) {
  return (
    <div
      style={style}
      className={classnames(
        styles['main'],
        styles[`main--${edge}`],
        styles[`main--${layout}`],
        className,
      )}
      data-testid={testId}
    >
      <div className={styles['slot']}>{start}</div>
      {layout === 'thirds' && (
        <div className={classnames(styles['slot'], styles['slot--center'])}>
          {center}
        </div>
      )}
      <div className={classnames(styles['slot'], styles['slot--end'])}>
        {end}
      </div>
    </div>
  );
}

export { ResourceBar };
